<script setup lang="ts">
import { ref, onMounted } from "vue";
import SvgIcon from "@SharedVueComponents/SvgIcon/SvgIcon.vue";
import OcmMessenger from "ocm_mercurius_messenger";
import { messengerPublicationTypes } from "@ocm/services/services.constants";
import type { Toast } from "@/SharedToastApp/types/toast.type";
import type { ToastMessengerData } from "@/shared/types/messenger.type";

const TOAST_DURATION = 4000;
const TOAST_LEAVE_DURATION = 360;
const _createUuid = () => (Math.random() + 1).toString(36).substring(2);
const _icons = new Map([
	["default", "checkmark-circled"],
	["info", "info"],
	["wishlist", "heart-filled"],
]);

let _toast: Toast | null = null;
const toast = ref<Toast>();

const _addToast = async ({ data }: ToastMessengerData) => {
	_toast = {
		id: _createUuid(),
		...data,
		icon: _icons.get(data.type ?? "default") as string,
	};

	// remove existing toast
	if (toast.value) {
		toast.value = undefined;
		await new Promise((resolve) => setTimeout(resolve, TOAST_LEAVE_DURATION));
	}

	toast.value = _toast;
	const id = _toast.id;
	setTimeout(() => {
		if (_toast?.id !== id) return;
		_removeToast();
	}, TOAST_DURATION);
};

const _removeToast = () => {
	_toast = null;
	toast.value = undefined;
};

onMounted(() => {
	OcmMessenger.subscribe(messengerPublicationTypes.ADD_TOAST, _addToast);
});
</script>

<template>
	<Teleport to="#shared-toast-container">
		<Transition name="shared-toast">
			<div
				v-if="toast"
				class="shared-toast ocm-body1 grid max-w-96 grid-cols-[4rem,1fr] gap-x-1 justify-self-center rounded-lg bg-gray-beton"
				:style="{ '--toast-leave-duration': TOAST_LEAVE_DURATION }"
			>
				<div
					class="grid items-center rounded-l-lg pl-3"
					:class="toast.type === 'info' ? 'diagonal-info' : 'diagonal-success'"
				>
					<SvgIcon :class="toast.type === 'wishlist' ? 'size-5' : 'size-4.5'" :icon-name="toast.icon" />
				</div>
				<div class="py-4 pr-4">
					<div class="shared-toast-message">{{ toast.message }}</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>

<style lang="postcss">
.shared-toast {
	--toast-enter-duration: 220ms;
	--toast-enter-opacity: 1;
	--toast-enter-translate: 0 100%;
	box-shadow: 0 5px 5px rgba(0, 0, 0, 0.2), 0 3px 14px rgba(0, 0, 0, 0.12), 0 8px 10px rgba(0, 0, 0, 0.14);

	@media (min-width: 64em) {
		--toast-enter-duration: 320ms;
		--toast-enter-opacity: 0.33;
		--toast-enter-translate: 100%;
	}

	@media (prefers-reduced-motion) {
		--toast-enter-duration: 440ms;
		--toast-enter-opacity: 0;
		--toast-enter-translate: 0;
	}
}

@keyframes shared-toast-slide-in {
	from {
		opacity: var(--toast-enter-opacity);
		translate: var(--toast-enter-translate);
	}
	to {
		opacity: unset;
		translate: unset;
	}
}

.shared-toast-enter-active {
	animation: shared-toast-slide-in var(--toast-enter-duration) ease-out forwards;

	#js-main-container:has(&) {
		overflow-x: clip;
	}
}

.shared-toast-leave-active {
	/* `toast-fade-in` is defined in '_toasts.scss' */
	animation: toast-fade-in calc(var(--toast-leave-duration, 360) * 1ms) ease-in reverse forwards;
}

.diagonal-success {
	background-image: conic-gradient(at 100% 0%, transparent 202deg, var(--color-success-background) 0deg);
}

.diagonal-info {
	background-image: conic-gradient(at 100% 0%, transparent 202deg, var(--color-gray-40) 0deg);
}

.shared-toast-message {
	overflow: hidden;
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;
}
</style>
